<template>
  <ClientOnly>
  <div class="px-[1.5rem] py-[2rem] md:pr-[2.1428rem] mx-auto md:px-6 md:pl-0 md:py-12">
    <div>
      <MediaCard :article="mappedArticle" titlePosition="top" :mediaType="props.mediaType" id="t5d-detail" />
    </div>
    <div v-if="props.mediaType =='video'" class="mt-[1.7857rem]">
      <YoutubeVideo :video-id="props.details?.videoId ?? ''" class="w-full"/>
    </div>
    <div class="detail-page t5d-rich-text" v-if="contentReady && mappedArticle?.content" v-html="mappedArticle.content"></div>
    <div v-else>
      <div class="detail-page t5d-rich-text text-ash-500 mt-6" :class="props.mediaType === 'podcast' ? 'pb-6': ''" v-html="mappedArticle?.content"></div>
      <div class="mt-2" v-if="props.mediaType === 'podcast'">
          <AudioPlayer
            id="detail-page"
            class="mb-4 mt-4"
            :src="props.details?.podcastUrl ?? ''"
            color="black"
            pageType="details"
          />
      </div>
      <div class="mt-[1.071rem]" v-if="props.mediaType === 'video'">
        <SocialShareGroup
          :url="absoluteUrl"
          :title="props.details?.title ?? ''"
          :videoId="props.details?.videoId ?? ''"
        />
      </div>
    </div>
  </div>
  </ClientOnly>
</template>

<script setup lang="ts">

import { computed, nextTick, onMounted, ref, watch, watchEffect } from 'vue'

import MediaCard from '../../organism/take-five-daily/mediaCard.vue'
import YoutubeVideo from '../../organism/youtubeVideo.vue'
import AudioPlayer from '../../atom/audioPlayer.vue'
import SocialShareGroup from '../../organism/SocialShareGroup.vue'

import type { ParsedBlog } from '~/components/utils/blogDetailsValue'
const props = defineProps<{
  details: ParsedBlog | null        
  mediaType: 'article' | 'video' | 'podcast'
}>()

const mappedArticle = computed(() => {
  const detData = props.details

  if (!detData) {
    return {
      title: '',
      url: '',
      image: '',
      excerpt: '',
      meta: '',
      publishAt: '',
      authorName: '',
      content: ''
    }
  }

  return {
    title: detData.title ?? '',
    url: detData.url ?? '',
    image: detData.mediaUrl ?? '',
    excerpt: detData.caption ?? '',
    meta: 'Weight Management',
    publishAt: detData.publishDate
      ? new Date(detData.publishDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : '',
    authorName: detData.author?.name ?? '',
    content: detData.content ?? ''
  }
})

const contentReady = ref(false)

watchEffect(() => {
  if (props.mediaType === 'article') {
    contentReady.value = true
  }
})

const applyReferencesClass = () => {
  if (typeof document === 'undefined') return

  document.querySelectorAll('.t5d-rich-text').forEach((container) => {
    const children = container.children
    if (children.length < 2) return

    const lastElement = children[children.length - 1]
    const previousElement = children[children.length - 2]
    const previousText = previousElement?.textContent?.trim().toLowerCase()

   if (
      lastElement?.tagName === 'OL' &&
      (previousText === 'references' || previousText === 'reference')
    ) {
      lastElement.classList.add('references-list')
    }
  })
}

const runReferencesEnhancement = async () => {
  await nextTick()
  applyReferencesClass()
}

onMounted(() => {
  runReferencesEnhancement()
})

watch(
  () => [mappedArticle.value.content, contentReady.value, props.mediaType],
  () => {
    runReferencesEnhancement()
  }
)

const absoluteUrl = computed(() => {
  const url = props.details?.url ?? ''
  if (url.startsWith('http')) return url
  return window.location.origin + url
})

</script>

<style scoped>
.t5d-rich-text :deep(ul) {
  list-style-type: disc;
  list-style-position: outside;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.t5d-rich-text :deep(ol) {
  list-style-type: decimal;
  list-style-position: outside;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.t5d-rich-text :deep(li) {
  margin: 0.25rem 0;
}

.t5d-rich-text :deep(hr) {
  border: 0;
  border-top: 1px solid #d1d5db;
  margin: 1.5rem 0;
}

.t5d-rich-text :deep(p),
.t5d-rich-text :deep(h1),
.t5d-rich-text :deep(h2),
.t5d-rich-text :deep(h3),
.t5d-rich-text :deep(h4),
.t5d-rich-text :deep(h5),
.t5d-rich-text :deep(h6),
.t5d-rich-text :deep(blockquote),
.t5d-rich-text :deep(table),
.t5d-rich-text :deep(ul),
.t5d-rich-text :deep(ol),
.t5d-rich-text :deep(hr) {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.t5d-rich-text :deep(ol.references-list),
.t5d-rich-text :deep(ol.references-list li) {
  font-size: 12px;
}

.t5d-rich-text :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
}

.t5d-rich-text :deep(td) {
  border: 1px solid #dcdcdc;
  padding: 8px 16px;
  text-align: left;
  vertical-align: top;
}

.t5d-rich-text :deep(thead td) {
  font-weight: 600;
  background-color: #f5f5f5;
}

.t5d-rich-text :deep(table:not(:has(thead)) tr:first-child td) {
  background-color: #f5f5f5;
  font-weight: 600;
}

.t5d-rich-text :deep(tbody tr:hover) {
  background-color: #fafafa;
}

@media (max-width: 768px) {
  .t5d-rich-text :deep(table) {
    display: block;
    width: max-content;
    min-width: 100%;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .t5d-rich-text :deep(table tr) {
    display: table-row;
  }

  .t5d-rich-text :deep(table td) {
    min-width: 140px;
    white-space: nowrap;
  }
}
</style>
